import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Content } from '@/components/cardWithThreeFooterBox';
import { GridWidget } from '@/components/GridWidget.tsx';
import { fromApiDateTime, Icon } from '@/utils';

import {
  ChartInterval,
  ChartProps,
  ChartType,
  HistogramChartConfigType,
  ICardDataState,
  PlotType,
} from '../types.ts';
import { HistogramHighChart } from './histogramCard/HistogramHighChart.tsx';

/**
 * Set config or use localStorage via useChartStorage
 * @param id
 * @param onOpenModal
 * @param onRemove
 * @param config
 * @param hideEditChart
 * @constructor
 */
export const HistogramCard = ({
  id,
  onOpenModal,

  config,
  hideEditChart,
  ...rest
}: ChartProps<HistogramChartConfigType>) => {
  const { t } = useTranslation(['common', 'chartConfig']);

  const [chartDataStat, setChartDataStat] = useState<ICardDataState>({
    isLoading: true,
    isError: false,
    isEmpty: false,
  });

  const [chartName, setChartName] = useState<string | undefined>(undefined);

  const chartConfig = useMemo<HistogramChartConfigType>(() => {
    const cfg = {
      ...defaultConfig,
      ...config,
    };

    // Ensure datetimes are local DateTimes (API strings may include an offset).
    if (typeof cfg.start === 'string') cfg.start = fromApiDateTime(cfg.start);
    if (typeof cfg.end === 'string') cfg.end = fromApiDateTime(cfg.end);

    return cfg;
  }, [config]);

  return (
    <GridWidget
      id={id}
      headerTitle={chartConfig?.title || ''}
      headerSubtitle={chartName}
      {...chartDataStat}
      emptyMessages={[t('chartConfig:noReadings'), t('chartConfig:noReadingsMessage')]}
      widgetId={id}
      {...rest}
      toolbarActionList={
        hideEditChart
          ? undefined
          : [
              {
                id: uuidv4(),
                label: t('common:edit'),
                onClick: onOpenModal,
                Icon: Icon.SlidersHorizontal,
              },
            ]
      }
    >
      <Content>
        <HistogramHighChart
          config={chartConfig}
          setChartDataStat={setChartDataStat}
          onChartNameChange={setChartName}
        />
      </Content>
    </GridWidget>
  );
};

// ************************************
// Constants
// ************************************
const defaultConfig: HistogramChartConfigType = {
  plotType: PlotType.HISTOGRAM,
  title: '',
  sensorId: '',
  provider: '',
  type: ChartType.HISTORICAL,
  interval: ChartInterval.Day,
};
